import { z } from "zod";
import { type UtmAttribution } from "@/lib/analytics/utm";
import { ruPhoneValidationError } from "@/lib/phone/ru-phone";

export const LEAD_FORM_SOURCES = [
  "name",
  "email",
  "phone",
  "company",
  "message",
] as const;

export type LeadFormSource = (typeof LEAD_FORM_SOURCES)[number];

export const leadFormFieldMapItemSchema = z.object({
  source: z.enum(LEAD_FORM_SOURCES),
  target: z.string().min(1).max(64),
  enabled: z.boolean().optional().default(true),
  required: z.boolean().optional().default(false),
});

export type LeadFormFieldMapItem = z.infer<typeof leadFormFieldMapItemSchema>;

export type LeadFormPayload = {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  message?: string;
  trace?: string;
  utm?: UtmAttribution;
};

export const LEAD_FORM_FIELD_LABELS: Record<LeadFormSource, string> = {
  name: "Имя",
  email: "Email",
  phone: "Телефон",
  company: "Компания",
  message: "Сообщение",
};

export const BITRIX_DEAL_FIELD_TARGETS = [
  { value: "TITLE", label: "Название сделки (TITLE)" },
  { value: "EMAIL", label: "Email контакта" },
  { value: "PHONE", label: "Телефон" },
  { value: "COMMENTS", label: "Комментарий (COMMENTS)" },
  { value: "SOURCE_DESCRIPTION", label: "Источник (SOURCE_DESCRIPTION)" },
] as const;

const DEFAULT_TARGET: Record<LeadFormSource, string> = {
  name: "TITLE",
  email: "EMAIL",
  phone: "PHONE",
  company: "COMMENTS",
  message: "COMMENTS",
};

const DEFAULT_ENABLED: Record<LeadFormSource, boolean> = {
  name: true,
  email: true,
  phone: true,
  company: true,
  message: true,
};

const DEFAULT_REQUIRED: Record<LeadFormSource, boolean> = {
  name: true,
  email: true,
  phone: false,
  company: false,
  message: false,
};

export const DEFAULT_FIELD_MAP: LeadFormFieldMapItem[] = LEAD_FORM_SOURCES.map(
  (source) => ({
    source,
    target: DEFAULT_TARGET[source],
    enabled: DEFAULT_ENABLED[source],
    required: DEFAULT_REQUIRED[source],
  }),
);

export type PublicLeadFormField = {
  source: LeadFormSource;
  label: string;
  enabled: boolean;
  required: boolean;
  bitrixTarget: string;
};

export function publicFieldsToFieldMap(
  fields: PublicLeadFormField[],
): LeadFormFieldMapItem[] {
  return fields.map((f) => ({
    source: f.source,
    target: f.bitrixTarget,
    enabled: f.enabled,
    required: f.required,
  }));
}

/** Полный набор полей для админки и API (всегда 5 строк). */
export function normalizeLeadFormFieldMap(
  value: unknown,
): LeadFormFieldMapItem[] {
  const raw = Array.isArray(value) ? value : [];
  const bySource = new Map<LeadFormSource, LeadFormFieldMapItem>();

  for (const item of raw) {
    const parsed = z
      .object({
        source: z.enum(LEAD_FORM_SOURCES),
        target: z.string().min(1).max(64),
        enabled: z.boolean().optional(),
        required: z.boolean().optional(),
      })
      .safeParse(item);
    if (!parsed.success) continue;
    bySource.set(parsed.data.source, {
      source: parsed.data.source,
      target: parsed.data.target,
      enabled: parsed.data.enabled ?? true,
      required: parsed.data.required ?? DEFAULT_REQUIRED[parsed.data.source],
    });
  }

  // Старые записи: в массиве только включённые поля, без enabled/required.
  const isLegacyMap =
    raw.length > 0 &&
    !raw.some(
      (item) =>
        item &&
        typeof item === "object" &&
        "enabled" in (item as Record<string, unknown>),
    );

  return LEAD_FORM_SOURCES.map((source) => {
    const existing = bySource.get(source);
    if (existing) {
      return {
        ...existing,
        enabled: isLegacyMap ? true : existing.enabled,
        required: isLegacyMap
          ? DEFAULT_REQUIRED[source]
          : existing.required,
      };
    }

    return {
      source,
      target: DEFAULT_TARGET[source],
      enabled: isLegacyMap ? false : DEFAULT_ENABLED[source],
      required: false,
    };
  });
}

export function fieldMapForBitrix(
  fieldMap: LeadFormFieldMapItem[],
): LeadFormFieldMapItem[] {
  return normalizeLeadFormFieldMap(fieldMap).filter((f) => f.enabled);
}

export function toPublicLeadFormFields(
  fieldMap: LeadFormFieldMapItem[],
): PublicLeadFormField[] {
  return normalizeLeadFormFieldMap(fieldMap).map((f) => ({
    source: f.source,
    label: LEAD_FORM_FIELD_LABELS[f.source],
    enabled: f.enabled,
    required: f.required,
    bitrixTarget: f.target,
  }));
}

export type LeadFormFieldErrors = Partial<
  Record<LeadFormSource, string | null>
>;

function validateEmail(email: string): string | null {
  const trimmed = email.trim();
  if (!trimmed) return "Укажите email";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return "Некорректный email";
  }
  return null;
}

/** Валидация значений формы по настройкам из интеграции. */
export function validateLeadFormValues(
  fieldMap: LeadFormFieldMapItem[],
  values: Record<LeadFormSource, string>,
): LeadFormFieldErrors {
  const errors: LeadFormFieldErrors = {};
  for (const field of normalizeLeadFormFieldMap(fieldMap)) {
    if (!field.enabled) continue;
    const value = values[field.source]?.trim() ?? "";
    if (field.source === "name") {
      if (field.required && !value) errors.name = "Укажите имя";
    } else if (field.source === "email") {
      if (field.required) {
        const err = validateEmail(value);
        if (err) errors.email = err;
      } else if (value) {
        const err = validateEmail(value);
        if (err) errors.email = err;
      }
    } else if (field.source === "phone") {
      if (field.required || value) {
        const err = ruPhoneValidationError(value);
        if (err) errors.phone = err;
      }
    } else if (field.source === "company") {
      if (field.required && !value) errors.company = "Укажите компанию";
    } else if (field.source === "message") {
      if (field.required && !value) errors.message = "Укажите сообщение";
    }
  }
  return errors;
}

/** Серверная валидация тела POST /api/public/lead-forms/[key]. */
export function parseLeadFormPayloadForCase(
  body: unknown,
  fieldMap: LeadFormFieldMapItem[],
):
  | { success: true; data: LeadFormPayload }
  | { success: false; error: string } {
  const record =
    body && typeof body === "object"
      ? (body as Record<string, unknown>)
      : null;
  if (!record) return { success: false, error: "Некорректное тело запроса" };

  const config = normalizeLeadFormFieldMap(fieldMap);
  const values: Record<LeadFormSource, string> = {
    name: String(record.name ?? "").trim(),
    email: String(record.email ?? "").trim(),
    phone: String(record.phone ?? "").trim(),
    company: String(record.company ?? "").trim(),
    message: String(record.message ?? "").trim(),
  };

  const fieldErrors = validateLeadFormValues(fieldMap, values);
  if (Object.values(fieldErrors).some(Boolean)) {
    return { success: false, error: "Проверьте поля формы" };
  }

  const enabled = config.filter((f) => f.enabled);
  const payload: LeadFormPayload = {
    name: values.name || "Заявка",
    email: values.email || "noreply@idealitika.ru",
  };

  if (enabled.some((f) => f.source === "phone") && values.phone) {
    payload.phone = values.phone;
  }
  if (enabled.some((f) => f.source === "company") && values.company) {
    payload.company = values.company;
  }
  if (enabled.some((f) => f.source === "message") && values.message) {
    payload.message = values.message;
  }

  const trace = String(record.trace ?? "").trim();
  if (trace) payload.trace = trace;

  const utmRaw = record.utm;
  if (utmRaw && typeof utmRaw === "object") {
    payload.utm = utmRaw as LeadFormPayload["utm"];
  }

  return { success: true, data: payload };
}

export function leadFormUiFromPublicFields(
  fields: PublicLeadFormField[],
): {
  showName: boolean;
  showEmail: boolean;
  showPhone: boolean;
  showCompany: boolean;
  showMessage: boolean;
  nameRequired: boolean;
  emailRequired: boolean;
  phoneRequired: boolean;
  companyRequired: boolean;
  messageRequired: boolean;
} {
  const bySource = new Map(fields.map((f) => [f.source, f]));
  const get = (source: LeadFormSource) =>
    bySource.get(source) ?? {
      enabled: false,
      required: false,
    };

  return {
    showName: get("name").enabled,
    showEmail: get("email").enabled,
    showPhone: get("phone").enabled,
    showCompany: get("company").enabled,
    showMessage: get("message").enabled,
    nameRequired: get("name").required,
    emailRequired: get("email").required,
    phoneRequired: get("phone").required,
    companyRequired: get("company").required,
    messageRequired: get("message").required,
  };
}
